import i18n from '../language/i18n';
import { API_URL } from '../config/connect';

/**
 * Maps frontend language codes to backend language codes
 * Frontend: kz, ru, en, cn
 * Backend: kk, ru, en, zh
 */
export function mapLanguageToBackend(frontendLang) {
  const langMap = {
    'kz': 'kk',  // Kazakh: frontend uses 'kz', backend uses 'kk'
    'ru': 'ru',  // Russian: same
    'en': 'en',  // English: same
    'cn': 'zh',  // Chinese: frontend uses 'cn', backend uses 'zh'
  };
  
  return langMap[frontendLang] || 'en';
}

/**
 * Gets current language from i18n and maps it to backend format
 */
export function getCurrentBackendLanguage() {
  const currentLang = i18n.language || localStorage.getItem('language') || 'kz';
  return mapLanguageToBackend(currentLang);
}

/**
 * Extracts error message from backend API response
 * Handles different error response formats:
 * - { error: { message: "...", error_code: "..." } } - New format
 * - { detail: { message: "...", error_code: "..." } } - FastAPI format
 * - { detail: { message: "..." } }
 * - { detail: "..." }
 * - { detail: [...] } (validation errors)
 * - { message: "..." }
 */
export function extractErrorMessage(errorResponse) {
  if (!errorResponse) {
    return null;
  }

  // Handle error object format: { error: { message: "...", error_code: "..." } }
  if (errorResponse.error) {
    if (typeof errorResponse.error === 'object' && !Array.isArray(errorResponse.error)) {
      if (errorResponse.error.message) {
        return {
          message: errorResponse.error.message,
          errorCode: errorResponse.error.error_code || null,
        };
      }
    }
  }

  // Handle detail object with message and error_code
  if (errorResponse.detail) {
    if (typeof errorResponse.detail === 'object' && !Array.isArray(errorResponse.detail)) {
      // Backend returns: { detail: { message: "...", error_code: "..." } }
      if (errorResponse.detail.message) {
        return {
          message: errorResponse.detail.message,
          errorCode: errorResponse.detail.error_code || null,
        };
      }
    }
    
    // Handle validation errors: { detail: [...] }
    if (Array.isArray(errorResponse.detail)) {
      const firstError = errorResponse.detail[0];
      if (firstError?.msg) {
        return {
          message: firstError.msg,
          errorCode: firstError.type || null,
        };
      }
      if (typeof firstError === 'string') {
        return {
          message: firstError,
          errorCode: null,
        };
      }
    }
    
    // Handle string detail: { detail: "..." }
    if (typeof errorResponse.detail === 'string') {
      return {
        message: errorResponse.detail,
        errorCode: null,
      };
    }
  }

  // Handle direct message: { message: "..." }
  if (errorResponse.message) {
    return {
      message: errorResponse.message,
      errorCode: null,
    };
  }

  return null;
}

/**
 * Extracts error message as a simple string
 * Returns the message string directly, or null if not found
 */
export function extractErrorMessageString(errorResponse) {
  const error = extractErrorMessage(errorResponse);
  return error?.message || null;
}

/**
 * Creates headers with Accept-Language for API requests
 * Automatically includes the current user's language
 */
export function createApiHeaders(customHeaders = {}) {
  const backendLang = getCurrentBackendLanguage();
  
  return {
    'Content-Type': 'application/json',
    'Accept-Language': backendLang,
    ...customHeaders,
  };
}

/**
 * Fetches error details from server using error code
 * Makes a separate request to get full error information
 * @param {string} errorCode - The error code from backend response
 * @param {string} category - Optional category (auth, user, announcement, etc.)
 * @returns {Promise<Object>} Error details with message
 */
export async function fetchErrorFromServer(errorCode, category = null) {
  if (!errorCode) {
    return null;
  }

  try {
    const backendLang = getCurrentBackendLanguage();
    
    // Build endpoint URL
    // Format: /api/v1/errors/{error_code}?lang={lang}&category={category}
    let endpoint = `${API_URL}/api/v1/errors/${errorCode}`;
    const params = new URLSearchParams({ lang: backendLang });
    if (category) {
      params.append('category', category);
    }
    endpoint += `?${params.toString()}`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: createApiHeaders(),
    });

    if (!response.ok) {
      // If error endpoint itself fails, return null
      console.warn(`Failed to fetch error details for code: ${errorCode}`);
      return null;
    }

    const errorData = await response.json();
    
    // Handle different response formats:
    // - { message: "..." }
    // - { error: { message: "..." } }
    // - { detail: { message: "..." } }
    if (errorData.message) {
      return { message: errorData.message };
    } else if (errorData.error?.message) {
      return { message: errorData.error.message };
    } else if (errorData.detail?.message) {
      return { message: errorData.detail.message };
    }
    
    return errorData;
  } catch (error) {
    console.warn(`Error fetching error details: ${error.message}`);
    return null;
  }
}

/**
 * Handles API response and extracts error, optionally fetching full error details
 * If error_code is present, makes additional request to get full error message
 * @param {Response} response - Fetch response object
 * @param {boolean} fetchErrorDetails - Whether to fetch error details from server if error_code exists
 * @param {string} url - The original request URL (for category extraction)
 * @returns {Promise} Parsed response data or throws error
 */
export async function handleApiResponse(response, fetchErrorDetails = true, url = null) {
  let data;
  
  try {
    data = await response.json();
  } catch (jsonError) {
    // If response is not JSON, throw network error
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return null;
  }

  if (!response.ok) {
    const errorInfo = extractErrorMessage(data);
    let errorMessage = errorInfo?.message || `HTTP ${response.status}: ${response.statusText}`;
    const errorCode = errorInfo?.errorCode;
    
    // If error_code exists and fetchErrorDetails is true, fetch full error details
    if (errorCode && fetchErrorDetails) {
      try {
        // Try to extract category from URL or use default
        // Use provided url parameter, or try response.url (if available), or null
        const requestUrl = url || response.url || null;
        const category = extractCategoryFromUrl(requestUrl);
        const errorDetails = await fetchErrorFromServer(errorCode, category);
        
        if (errorDetails?.message) {
          errorMessage = errorDetails.message;
        }
      } catch (fetchError) {
        // If fetching error details fails, use original message
        console.warn('Failed to fetch error details:', fetchError);
      }
    }
    
    const error = new Error(errorMessage);
    error.errorCode = errorCode;
    error.statusCode = response.status;
    error.responseData = data;
    throw error;
  }

  return data;
}

/**
 * Extracts category from API URL
 * @param {string} url - API endpoint URL
 * @returns {string|null} Category name or null
 */
function extractCategoryFromUrl(url) {
  if (!url) return null;
  
  // Extract category from URL patterns like /api/v1/{category}/...
  const match = url.match(/\/api\/v1\/([^/]+)/);
  if (match && match[1]) {
    const category = match[1];
    // Map common endpoint names to category names
    const categoryMap = {
      'auth': 'auth',
      'user': 'user',
      'announcement': 'announcement',
      'announcements': 'announcement',
      'category': 'category',
      'categories': 'category',
      'promotion': 'promotion',
      'promotions': 'promotion',
      'location': 'location',
      'locations': 'location',
      'device': 'device',
      'devices': 'device',
      'demand': 'demand',
      'demands': 'demand',
      'favorites': 'favorites',
      'payment': 'payment',
      'payments': 'payment',
      'admin': 'admin',
      'chat': 'chat',
      'profile': 'profile',
    };
    
    return categoryMap[category] || category;
  }
  
  return null;
}

/**
 * Wrapper for fetch that automatically:
 * - Adds Accept-Language header
 * - Handles error extraction
 * - Fetches full error details from server if error_code exists
 * - Returns parsed JSON on success
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options
 * @param {boolean} options.fetchErrorDetails - Whether to fetch error details from server (default: true)
 */
export async function apiFetch(url, options = {}) {
  const { fetchErrorDetails = true, ...fetchOptions } = options;
  const headers = createApiHeaders(fetchOptions.headers);
  
  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  // Pass URL as parameter instead of trying to set response.url (which is read-only)
  return handleApiResponse(response, fetchErrorDetails, url);
}

